import React, { forwardRef, useCallback, useState } from 'react';
import { useForm } from 'react-final-form';
import { identity } from 'ramda';

import { Control } from 'js/components/common/Fields/Control';
import ValidationIcon from 'js/components/common/Fields/Icons/ValidationIcon';
import VisibilityIcon from 'js/components/common/Fields/Icons/VisibilityIcon';
import { InputShallow } from 'js/app/components/common/Fields/Input/components/InputShallow';

import { clsx } from 'js/app/utils/clsx.util';
import { clsb } from 'js/utils/classNames.util';
import { useTranslations } from 'js/hooks/i18n';
import withField from 'js/components/common/Fields/withField';

import style from './Input.scss';

export const InputBare = forwardRef(({
    /* rff render props */
    input,
    meta,

    /* user provided props */
    id,
    name,
    label,
    hint,
    readOnly,
    bottomHint,
    type = 'text',
    className = '',
    classNames = {},
    onClick,
    infoTooltip,
    currencySymbol,
    showPasswordEye = true,
    onMouseDown,
    placeholder = '',
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,

    /* buzz props */
    rightBlock,
    leftBlock,
    bottomText,
    isBorderHidden,
    errorRightBlock,
    isShowValidationIcon = false,
    isShowValidIcon = false,
    isHideForcedPlaceholder = false,
    isForced = false,
    resetFieldState = identity,

    ...inputDomProps
}, ref) => {
    const translate = useTranslations('playtech.mobile.system.input.');
    const { active, touched, error, submitting } = meta;
    const showPasswordIcon = showPasswordEye && type === 'password';
    const [isVisible, setIsVisible] = useState(false);
    const buildCls = clsb([style, classNames]);
    const toggleVisible = () => {
        handlerButtonBlur();
        setIsVisible(!isVisible);
    };
    const dataCurrencyAttribute = currencySymbol ? { 'data-currency-symbol': currencySymbol } : {};
    const preventClick = useCallback(event => event.preventDefault(), []);

    const form = useForm();

    const handlerButtonBlur = () => {
        form.mutators.buttonBlur(name);
    };

    return (
        <Control {...{
            id,
            meta,
            readOnly,
            className,
            onClick,
            label,
            hint,
            bottomHint,
            animate: true,
            infoTooltip,
            isLabelAsteriskEnabled,

            bottomText,
            isBorderHidden,

            ...dataCurrencyAttribute
        }}
        classNames={{
            error: style.error,
            ...classNames
        }}>
            {leftBlock && <div className={buildCls(['leftBlock'])}>{leftBlock}</div>}

            <InputShallow
                {...input}
                {...inputDomProps}
                id={id}
                name={name}
                readOnly={readOnly}
                tabIndex={readOnly ? -1 : 0}
                type={isVisible ? 'text' : type}
                aria-label={inputDomProps['aria-label'] || translate('aria-label')}
                {...submitting ? { disabled: true } : {}}
                onMouseDown={readOnly ? preventClick : onMouseDown}
                className={
                    clsx(
                        classNames.input,
                        { [style.isLeftBlock]: leftBlock },
                        { [style.isHideForcedPlaceholder]: isHideForcedPlaceholder }
                    )
                }
                ref={ref}
            />

            {type !== 'checkbox' && placeholder && (
                <span className={clsx(
                    style.placeholder,
                    classNames.placeholder,
                    {
                        [style.forced]: input.value.length || isForced,
                        forced: input.value.length,
                        [style.showPasswordIcon]: showPasswordIcon
                    }
                )}>
                    {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                </span>
            )}

            {isShowValidationIcon && type !== 'checkbox' && (touched || input.value || isShowValidIcon) && !active && !readOnly && (
                <ValidationIcon
                    className={classNames?.validationIcon}
                    isValid={!error}
                    animate
                    onClick={() => resetFieldState(name)}
                    aria-label={error}
                    tabIndex={error ? 0 : -1}
                />
            )}

            {rightBlock && (
                <div
                    className={clsx(
                        style.rightBlock,
                        classNames.rightBlock,
                        { [style.rightBlockError]: errorRightBlock }
                    )}
                    onClick={handlerButtonBlur}
                >
                    {rightBlock()}
                </div>
            )}

            {showPasswordIcon && (
                <VisibilityIcon
                    onClick={toggleVisible}
                    isVisible={isVisible}
                    className={style.visibilityIcon}
                />
            )}
        </Control>
    );
});

export const Input = withField(InputBare);
